// ============================================================================
// parseJsonSafe - robust JSON parser for LLM responses
// ----------------------------------------------------------------------------
// LLM JSON output is unreliable. Common failure modes:
//   1. Bare JSON                                  → JSON.parse works
//   2. Wrapped in markdown code fences            → strip ```...``` and retry
//   3. Trailing commas                            → strip and retry
//   4. Comments (// or /* */)                     → strip and retry
//   5. Literal newlines/tabs INSIDE string values → escape and retry
//   6. Smart quotes ('curly' instead of "straight") → normalize and retry
//   7. Multiple objects, leading commentary       → bracket-count to find first object
//   8. Prose preamble before the object           → strip pre-`{` / pre-`[`
//   9. Truncated mid-array                        → salvage largest valid prefix
//
// This parser tries each strategy in order. Returns null on unrecoverable failure.
//
// Opus-hardening (2026-04-20): OpenRouter silently ignores
// `response_format: json_object` for Claude Opus, so the model emits markdown
// fences, prose preambles, smart quotes, and trailing commas more often than
// strict-mode models. The pre-strip + smart-quote + salvage passes below
// rescue those responses without changing the API contract. Kill switch:
// `window.qm_disable_opus_parse_hardening = true` falls back to the legacy
// strategy set.
// ============================================================================

/**
 * Walk a string and escape literal control characters that occur INSIDE string
 * literals. JSON spec says strings cannot contain raw newlines/tabs/CR - they
 * must be escaped as \n, \t, \r. LLMs frequently emit raw newlines.
 *
 * This walks the input character by character, tracking string vs non-string
 * context, and escapes any control char it finds inside a string.
 */
function escapeControlCharsInStrings(text: string): string {
    if (!text) return text;
    let result = '';
    let inString = false;
    let escape = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i]!;
        if (escape) {
            result += ch;
            escape = false;
            continue;
        }
        if (ch === '\\') {
            result += ch;
            escape = true;
            continue;
        }
        if (ch === '"') {
            result += ch;
            inString = !inString;
            continue;
        }
        if (inString) {
            if (ch === '\n') { result += '\\n'; continue; }
            if (ch === '\r') { result += '\\r'; continue; }
            if (ch === '\t') { result += '\\t'; continue; }
            // Other control chars (0x00-0x1F) - escape as \uXXXX
            const code = ch.charCodeAt(0);
            if (code < 0x20) {
                result += '\\u' + code.toString(16).padStart(4, '0');
                continue;
            }
        }
        result += ch;
    }
    return result;
}

/**
 * Strip JS-style line and block comments OUTSIDE of string literals.
 */
function stripComments(text: string): string {
    if (!text) return text;
    let result = '';
    let inString = false;
    let escape = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const next = text[i + 1];
        if (escape) {
            result += ch;
            escape = false;
            continue;
        }
        if (inString) {
            if (ch === '\\') { result += ch; escape = true; continue; }
            if (ch === '"') inString = false;
            result += ch;
            continue;
        }
        if (ch === '"') {
            inString = true;
            result += ch;
            continue;
        }
        // // line comment
        if (ch === '/' && next === '/') {
            while (i < text.length && text[i] !== '\n') i++;
            continue;
        }
        // /* block comment */
        if (ch === '/' && next === '*') {
            i += 2;
            while (i < text.length - 1 && !(text[i] === '*' && text[i + 1] === '/')) i++;
            i++;
            continue;
        }
        result += ch;
    }
    return result;
}

/**
 * Normalize smart quotes to straight quotes.
 * Walks the input tracking string context so inner curly-doubles inside an
 * already-opened STRAIGHT string become escaped content (`\"`) rather than
 * premature terminators — preserving payloads like
 * `{"desc": "he said \u201Chi\u201D"}` where the parsed value must read as
 * `he said "hi"`. Structural curly-doubles (acting as delimiters the LLM
 * should have emitted as `"`) flip to `"` by finding the run's far pair:
 * when a value wraps a quoted phrase with more curly-doubles inside, the
 * first and last curly-double in the stretch up to the next structural
 * delimiter are the real boundaries; the pairs between them are content.
 * Curly singles only flip outside strings so apostrophes in values stay.
 */
function normalizeQuotes(text: string): string {
    if (!text) return text;
    const CURLY_DBL = /[\u201C\u201D]/;
    const isStructural = (c: string): boolean => c === ',' || c === '}' || c === ']' || c === ':';
    let out = '';
    let inString = false;
    let escape = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i]!;
        if (escape) { out += ch; escape = false; continue; }
        if (ch === '\\') { out += ch; escape = true; continue; }
        if (ch === '"') { out += ch; inString = !inString; continue; }
        if (CURLY_DBL.test(ch)) {
            if (inString) { out += '\\"'; continue; } // content inside a straight-quoted string
            // Find the end of this structural string: scan forward to the next
            // structural delimiter at top level, then take the LAST curly-double
            // before it as the real closer. Any curly-doubles between become
            // escaped content.
            let j = i + 1;
            let lastCurly = -1;
            let innerEscape = false;
            while (j < text.length) {
                const cj = text[j]!;
                if (innerEscape) { innerEscape = false; j++; continue; }
                if (cj === '\\') { innerEscape = true; j++; continue; }
                if (CURLY_DBL.test(cj)) { lastCurly = j; j++; continue; }
                if (cj === '"') break; // straight quote is never inside a curly-string
                if (isStructural(cj)) break;
                j++;
            }
            if (lastCurly >= 0) {
                out += '"';
                const inner = text.slice(i + 1, lastCurly)
                    .replace(/"/g, '\\"') // pre-existing straight quotes become escaped content first
                    .replace(/[\u201C\u201D]/g, '\\"'); // then nested curly-doubles become escaped content
                out += inner;
                out += '"';
                i = lastCurly;
                continue;
            }
            out += '"'; inString = true; // no closer found — treat as open and let downstream logic cope
            continue;
        }
        if (!inString && (ch === '\u2018' || ch === '\u2019')) { out += "'"; continue; }
        out += ch;
    }
    return out;
}

/**
 * Find the first balanced top-level {...} block by bracket counting.
 * Skips braces inside strings. Returns null if not found.
 */
function extractFirstObject(text: string): string | null {
    if (!text) return null;
    const firstBrace = text.indexOf('{');
    if (firstBrace < 0) return null;
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = firstBrace; i < text.length; i++) {
        const ch = text[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) {
                return text.substring(firstBrace, i + 1);
            }
        }
    }
    return null;
}

/**
 * Strip trailing commas before } and ]
 */
function stripTrailingCommas(text: string): string {
    return text.replace(/,(\s*[}\]])/g, '$1');
}

/**
 * Aggressive pre-strip for Opus-style drift: peel markdown fences off the
 * outside, drop any prose that precedes the first `{` or `[`, and drop
 * anything trailing the matching close bracket at depth 0.
 *
 * Returns the cleaned slice, or the original text if no bracket is found.
 */
function preStripOpusDrift(text: string): string {
    if (!text || typeof text !== 'string') return text;
    let t = text;

    // why: Opus likes to wrap the entire response in ```json ... ``` even
    // when response_format is json_object. Peel one layer.
    const fenceRe = /```(?:json|JSON)?\s*([\s\S]*?)\s*```/;
    const fence = t.match(fenceRe);
    if (fence && fence[1]) t = fence[1];

    // why: if only the opening fence survived (truncated before closer),
    // strip the header so the bracket scan below can still find the body.
    t = t.replace(/^[\s\S]*?```(?:json|JSON)?\s*/, (match, ...rest) => {
        // Only strip if there's no closing fence — otherwise the regex above
        // already handled it. This is a best-effort fallback.
        return t.indexOf('```', match.length) >= 0 ? match : '';
    });

    // Find the first structural opener ({ or [) and drop anything before it.
    const firstBrace = t.indexOf('{');
    const firstBracket = t.indexOf('[');
    let firstOpen = -1;
    let openChar = '';
    let closeChar = '';
    if (firstBrace >= 0 && (firstBracket < 0 || firstBrace < firstBracket)) {
        firstOpen = firstBrace; openChar = '{'; closeChar = '}';
    } else if (firstBracket >= 0) {
        firstOpen = firstBracket; openChar = '['; closeChar = ']';
    }
    if (firstOpen < 0) return t;
    t = t.slice(firstOpen);

    // Walk forward to the matching close at depth 0, then drop any trailing
    // prose. Respect strings so braces inside values don't confuse us.
    let depth = 0;
    let inString = false;
    let escape = false;
    let lastClose = -1;
    for (let i = 0; i < t.length; i++) {
        const ch = t[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === openChar) depth++;
        else if (ch === closeChar) {
            depth--;
            if (depth === 0) { lastClose = i; break; }
        }
    }
    if (lastClose >= 0) t = t.slice(0, lastClose + 1);
    return t;
}

/**
 * Salvage a partial object when the LLM response was truncated mid-array
 * (typically because maxTokens was reached before the closing brackets).
 *
 * Strategy: find a top-level `"<key>": [` opener, walk forward collecting
 * every complete balanced object, then rebuild a minimal valid JSON with
 * ONLY the key siblings we've already seen plus the salvaged array.
 *
 * Returns the salvaged JSON string, or null if nothing salvageable found.
 *
 * This is the fallback for triage-style outputs where losing the last item
 * of an array is strictly better than losing the whole response.
 */
function salvageTruncatedArray(text: string, keyName = 'decisions'): string | null {
    if (!text || typeof text !== 'string') return null;
    // Find the array opener for the named key: `"decisions"` followed by : and [
    const keyRe = new RegExp('"' + keyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '"\\s*:\\s*\\[');
    const match = keyRe.exec(text);
    if (!match) return null;
    const arrayStart = match.index + match[0].length; // char AFTER the [

    // Walk forward, collecting each complete balanced { ... } object.
    const completeObjects = [];
    let i = arrayStart;
    while (i < text.length) {
        // Skip whitespace and commas between objects
        while (i < text.length && /[\s,]/.test(text[i]!)) i++;
        if (i >= text.length) break;
        if (text[i] === ']') break; // array already closed - nothing to salvage
        if (text[i] !== '{') break; // unexpected content - stop

        // Bracket-count this object
        const objStart = i;
        let depth = 0;
        let inString = false;
        let escape = false;
        let closed = false;
        for (; i < text.length; i++) {
            const ch = text[i];
            if (escape) { escape = false; continue; }
            if (ch === '\\') { escape = true; continue; }
            if (ch === '"') { inString = !inString; continue; }
            if (inString) continue;
            if (ch === '{') depth++;
            else if (ch === '}') {
                depth--;
                if (depth === 0) {
                    completeObjects.push(text.substring(objStart, i + 1));
                    i++;
                    closed = true;
                    break;
                }
            }
        }
        if (!closed) break; // truncated inside this object - stop
    }

    if (completeObjects.length === 0) return null;

    // Rebuild a minimal valid JSON with just the salvaged array.
    return '{"' + keyName + '":[' + completeObjects.join(',') + ']}';
}

/**
 * Generic "largest valid prefix" salvage: walk the input and find the last
 * point at which all open brackets have been closed (depth returns to 0).
 * If we never get there, synthesize closers for any still-open brackets.
 *
 * Returns a candidate string ready for JSON.parse, or null.
 */
function salvageLargestValidPrefix(text: string): string | null {
    if (!text || typeof text !== 'string') return null;
    let depth = 0;
    let inString = false;
    let escape = false;
    const stack = []; // track whether each open was { or [
    let lastBalancedEnd = -1;
    let started = false;
    // why: remember the last index at which we were guaranteed to be at
    // top-level (outside any string, between structural tokens). If the
    // tail is a truncated string or partial key-value, we can rewind here
    // and close the brackets cleanly.
    let lastSafeTopLevelIdx = -1;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{' || ch === '[') {
            stack.push(ch);
            depth++;
            started = true;
        } else if (ch === '}' || ch === ']') {
            stack.pop();
            depth--;
            if (depth === 0 && started) lastBalancedEnd = i;
            // A closed sibling inside an array/object is a safe rewind point;
            // the next char is either `,` or the closer of the parent.
            if (started) lastSafeTopLevelIdx = i;
        }
    }
    // Prefer a clean balanced prefix when one exists.
    if (lastBalancedEnd > 0) {
        return text.slice(0, lastBalancedEnd + 1);
    }
    if (stack.length === 0) return null;
    // why: if the tail is still inside a string (unterminated "...), we
    // can't simply append closers — rewind to the last complete sibling
    // (e.g. the `}` of the previous array item) and close from there.
    // Secondary rewind (2026-04-20): when truncation happens inside the
    // FIRST array item (lastSafeTopLevelIdx === -1, no complete sibling
    // exists), find the last unescaped `"` in the raw and truncate there —
    // the resulting partial string gets closed with `"` and the remaining
    // open brackets from the tracked stack are appended. This rescues the
    // outer structure with a (possibly truncated) narrative field rather
    // than returning null for the entire response.
    let cutoff;
    let needsStringCloser = false;
    if (inString && lastSafeTopLevelIdx >= 0) {
        cutoff = lastSafeTopLevelIdx + 1;
    } else if (inString && lastSafeTopLevelIdx < 0) {
        // Secondary rewind: no complete sibling exists, but we're inside
        // an unterminated string. Locate the last `"` not preceded by an
        // odd run of backslashes (i.e. actually unescaped) — that is the
        // OPENING quote of the unterminated string. Keep all partial
        // content written after it, escape any control chars that would
        // make the string invalid, append `"` to close, then close the
        // remaining open brackets. This preserves the partial narrative
        // instead of discarding it.
        let lastQuote = -1;
        for (let i = text.length - 1; i >= 0; i--) {
            if (text[i] !== '"') continue;
            let bs = 0;
            let k = i - 1;
            while (k >= 0 && text[k] === '\\') { bs++; k--; }
            if (bs % 2 === 0) { lastQuote = i; break; }
        }
        if (lastQuote < 0) return null;
        cutoff = text.length; // keep the partial content
        needsStringCloser = true;
    } else {
        cutoff = text.length;
        while (cutoff > 0) {
            const c = text[cutoff - 1]!;
            if (c === ',' || /\s/.test(c)) { cutoff--; continue; }
            break;
        }
    }
    let salvaged = text.slice(0, cutoff);
    if (needsStringCloser) {
        // Strip trailing incomplete unicode/control escape fragments so
        // `"...\` or `"...\u00` don't produce a SyntaxError after we add `"`.
        salvaged = salvaged.replace(/\\u[0-9a-fA-F]{0,3}$/, '').replace(/\\$/, '');
        // Escape raw control chars in the ENTIRE salvaged slice so the
        // now-closed partial string is valid JSON content. Downstream
        // escapeControlCharsInStrings already handles this when called via
        // strategy 9, but here we close the string first so callers can
        // JSON.parse the candidate directly.
        salvaged = escapeControlCharsInStrings(salvaged);
        salvaged += '"';
    }
    // Strip any trailing comma after rewind.
    salvaged = salvaged.replace(/,\s*$/, '');
    // Recompute remaining-open stack for the trimmed slice so we close
    // exactly what's still open, not what was open in the untrimmed text.
    const trimmedStack = [];
    let ts_inString = false;
    let ts_escape = false;
    for (let i = 0; i < salvaged.length; i++) {
        const ch = salvaged[i];
        if (ts_escape) { ts_escape = false; continue; }
        if (ch === '\\') { ts_escape = true; continue; }
        if (ch === '"') { ts_inString = !ts_inString; continue; }
        if (ts_inString) continue;
        if (ch === '{' || ch === '[') trimmedStack.push(ch);
        else if (ch === '}' || ch === ']') trimmedStack.pop();
    }
    for (let j = trimmedStack.length - 1; j >= 0; j--) {
        salvaged += trimmedStack[j] === '{' ? '}' : ']';
    }
    return salvaged;
}

/**
 * Log helper with optional context tag.
 */
function hardeningLog(
    level: 'log' | 'info' | 'warn' | 'error' | 'debug',
    context: string,
    message: string,
    detail?: unknown,
): void {
    const tag = context ? `[parseJsonSafe:${context}]` : '[parseJsonSafe]';
    const fn = (typeof console !== 'undefined' && console[level]) ? console[level] : console.log;
    if (detail !== undefined) fn(`${tag} ${message}`, detail);
    else fn(`${tag} ${message}`);
}

/**
 * Kill-switch probe. Checks window flag in browser and globalThis in Node so
 * the test script can toggle too. Synchronous and cheap.
 */
function isHardeningDisabled(): boolean {
    try {
        if (typeof window !== 'undefined' && (window as any).qm_disable_opus_parse_hardening) return true;
        if (typeof globalThis !== 'undefined' && (globalThis as any).qm_disable_opus_parse_hardening) return true;
    } catch { /* no window in worker contexts */ }
    return false;
}

/**
 * Options for {@link parseJsonSafe}.
 */
export interface ParseJsonSafeOptions {
    /**
     * If provided, attempt to salvage a truncated response by extracting
     * complete objects from the named top-level array key(s). Useful for
     * triage-style outputs where losing the last item is better than losing
     * everything. Examples: "decisions", ["decisions", "proposals"].
     */
    salvageArrayKeys?: string | string[];
    /**
     * Explicitly allow generic partial-object salvage. Keep false for
     * quote/cost/learning state writes.
     */
    allowPrefixSalvage?: boolean;
    /**
     * Caller name for log traceability, e.g. 'strategy', 'wms', 'wms-step1'.
     * Tagged onto console output only.
     */
    context?: string;
}

/**
 * Try a sequence of parse strategies, returning the first successful result.
 * Returns null if every strategy fails.
 *
 * @param input  The raw string to parse.
 * @param opts   See {@link ParseJsonSafeOptions}.
 */
export function parseJsonSafe(input: string, opts: ParseJsonSafeOptions = {}): any {
    if (!input || typeof input !== 'string') return null;
    const context = opts.context || '';
    const hardeningOff = isHardeningDisabled();

    // Strategy 1: Parse as-is (happy path)
    try { return JSON.parse(input); } catch {}

    // ─── Opus-hardening Pass 1: pre-strip fences + prose preamble/trail ───
    // why: OpenRouter Opus emits ```json ... ``` + leading prose + trailing
    // "Let me know if…" commentary. The existing fenced-match strategy only
    // handled the inner-content case; it didn't help when the fence was
    // malformed or the prose drifted past the object boundary.
    if (!hardeningOff) {
        const pre = preStripOpusDrift(input);
        if (pre && pre !== input) {
            try { return JSON.parse(pre); } catch {}
            try { return JSON.parse(stripTrailingCommas(escapeControlCharsInStrings(pre))); } catch {}
            try { return JSON.parse(normalizeQuotes(stripComments(escapeControlCharsInStrings(stripTrailingCommas(pre))))); } catch {}
        }
    }

    // Strategy 2: Strip markdown code fences (both complete AND truncated - a
    // fence that opens with ```json but got cut off before the closing ``` is
    // a real-world case when providers hit maxTokens).
    const fenced = input.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (fenced && fenced[1]) {
        try { return JSON.parse(fenced[1]); } catch {}
        // ...with control-char escaping
        try { return JSON.parse(escapeControlCharsInStrings(fenced[1])); } catch {}
    }
    // Opening fence only, no closer - strip the fence header and keep going
    const openFence = input.match(/^[\s\S]*?```(?:json)?\s*/);
    const deFenced = openFence ? input.slice(openFence[0].length) : input;

    // Strategy 3: Extract first balanced object
    const obj = extractFirstObject(deFenced);
    if (obj) {
        try { return JSON.parse(obj); } catch {}
        // Strategy 4: + escape control chars inside strings (most common LLM mistake)
        try { return JSON.parse(escapeControlCharsInStrings(obj)); } catch {}
        // Strategy 5: + strip trailing commas
        try { return JSON.parse(stripTrailingCommas(obj)); } catch {}
        // Strategy 6: + escape control chars + strip trailing commas
        try { return JSON.parse(stripTrailingCommas(escapeControlCharsInStrings(obj))); } catch {}
        // Strategy 7: + strip comments
        try { return JSON.parse(stripComments(escapeControlCharsInStrings(stripTrailingCommas(obj)))); } catch {}
        // Strategy 8: + normalize smart quotes
        try { return JSON.parse(normalizeQuotes(stripComments(escapeControlCharsInStrings(stripTrailingCommas(obj))))); } catch {}
    }

    // Strategy 9: Apply ALL fixes to the entire input
    try {
        const fixed = normalizeQuotes(stripComments(escapeControlCharsInStrings(stripTrailingCommas(deFenced))));
        return JSON.parse(fixed);
    } catch {}

    const salvageKeys = Array.isArray(opts.salvageArrayKeys)
        ? opts.salvageArrayKeys
        : (typeof opts.salvageArrayKeys === 'string' ? [opts.salvageArrayKeys] : null);

    // ─── Opus-hardening Pass 3: largest-valid-prefix salvage ──────────────
    // why: when Opus truncates mid-array with no specific salvageArrayKeys
    // hint, we can still often recover a balanced prefix. Because that can
    // produce an incomplete but syntactically valid object, callers must opt
    // in with allowPrefixSalvage.
    if (!hardeningOff && opts.allowPrefixSalvage === true && !salvageKeys) {
        const pre = preStripOpusDrift(input);
        const basis = pre || deFenced;
        const prepped = stripTrailingCommas(escapeControlCharsInStrings(basis));
        const candidate = salvageLargestValidPrefix(prepped);
        if (candidate && candidate !== prepped) {
            try {
                const parsed = JSON.parse(candidate);
                Object.defineProperty(parsed, '__salvaged', { value: 'prefix', enumerable: false });
                hardeningLog('info', context, 'recovered from truncation via largest-valid-prefix salvage', {
                    originalLen: input.length,
                    salvagedLen: candidate.length,
                });
                return parsed;
            } catch {}
        }
    }

    // Strategy 10: Truncation salvage - for triage-style outputs, recover
    // whatever complete array items we can. Opt-in via opts.salvageArrayKeys
    // because callers that want strict parsing should NOT get a partial result.
    if (salvageKeys && salvageKeys.length > 0) {
        // Apply the same control-char and trailing-comma normalisation first
        // so malformed inner strings don't defeat the salvage bracket-walk.
        const prepped = stripTrailingCommas(escapeControlCharsInStrings(deFenced));
        for (const key of salvageKeys) {
            const salvaged = salvageTruncatedArray(prepped, key);
            if (salvaged) {
                try {
                    const parsed = JSON.parse(salvaged);
                    // Flag the result so callers can log truncation explicitly.
                    Object.defineProperty(parsed, '__truncated', { value: true, enumerable: false });
                    return parsed;
                } catch {}
            }
        }
    }

    return null;
}

// Re-export the helpers in case any caller wants them individually
export {
    escapeControlCharsInStrings,
    stripComments,
    normalizeQuotes,
    extractFirstObject,
    stripTrailingCommas,
    salvageTruncatedArray,
    preStripOpusDrift,
    salvageLargestValidPrefix,
};

export default parseJsonSafe;
